import { ErrorBoundary } from '@sentry/react';
import { Button } from '@statsbomb/kitbag-components';
import { KeysToCamelCase, UserNavOverrides } from '@statsbomb/parachute-types';
import { useAtomValue, useSetAtom } from 'jotai';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useTrackExperienceChange } from '@/analytics/snowplow/hooks/useTrackExperienceChange';
import { ViewConfigurationSource, ViewType } from '@/analytics/snowplow/types';
import { patchUserNavOverridesAtom } from '@/atoms/mutations/user/user';
import { userNavOverridesAtom } from '@/atoms/queries/user';
import { toastDataAtom } from '@/atoms/toast';
import { classicUrl } from '@/utils/classicLinks';

interface SwitchToClassicButtonProps {
  path: string;
  navOverrides: Partial<{ [key in keyof KeysToCamelCase<UserNavOverrides>]: boolean | undefined }>;
}

const SwitchToClassicButtonBase = ({ onClick }: { onClick?: () => void }) => {
  const { t } = useTranslation('experience');

  return (
    <Button size="small" variant="primary" onClick={onClick} isPending={!onClick}>
      {t('switchToClassicButton')}
    </Button>
  );
};

const getViewConfigurationDetails = (path: string) => {
  if (path.includes('scout')) {
    return {
      configurationSource: ViewConfigurationSource.EXPLORE_LANDING_PAGE,
      viewType: ViewType.PLAYER_SCOUT,
    };
  }
  if (path.includes('team')) {
    return {
      configurationSource: ViewConfigurationSource.TEAM_DASHBOARD_BUTTON,
      viewType: ViewType.TEAM,
    };
  }
  // player dashboard by default
  return {
    configurationSource: ViewConfigurationSource.PLAYER_DASHBOARD_BUTTON,
    viewType: ViewType.PLAYER,
  };
};

const SwitchToClassicButtonWithData = ({ navOverrides, path }: SwitchToClassicButtonProps) => {
  const userNavOverrides = useAtomValue(userNavOverridesAtom);
  const { mutateAsync: patchUserNavOverrides } = useAtomValue(patchUserNavOverridesAtom);
  const { configurationSource, viewType } = getViewConfigurationDetails(path);

  const trackExperienceChange = useTrackExperienceChange(viewType, configurationSource);

  const setToastData = useSetAtom(toastDataAtom);

  const onClick = async () => {
    const isSaved = await patchUserNavOverrides({
      ...userNavOverrides,
      ...navOverrides,
    })
      .then(({ isSuccess }) => isSuccess)
      .catch(() => false);
    // Keep the user where they are if the preference wasn't saved, so it can't disagree with the page they're on.
    if (!isSaved) {
      setToastData({ toastName: 'switchExperience', type: 'error' });
      return;
    }
    // Tracking is best-effort: a failed or slow analytics call must not block the redirect.
    trackExperienceChange().catch(() => {});
    window.location.href = classicUrl(path);
  };

  return <SwitchToClassicButtonBase onClick={onClick} />;
};

export const SwitchToClassicButton = (props: SwitchToClassicButtonProps) => (
  <ErrorBoundary fallback={<SwitchToClassicButtonBase />}>
    <Suspense fallback={<SwitchToClassicButtonBase />}>
      <SwitchToClassicButtonWithData {...props} />
    </Suspense>
  </ErrorBoundary>
);
